import type { SiteConfig } from '../../src/site/types';

/** koreasnowwatch.com */
const config: SiteConfig = {
  id: 'kr',
  brand: {
    name: 'KoreaSnowWatch',
    shortName: 'KoreaSnow',
    logoText: { lead: 'KoreaSnow', accent: 'Watch' },
    alternateNames: ['Korea Snow Watch'],
    description: {
      en: 'Live webcams, snow depth and weather forecasts for Korean ski resorts.',
      ja: '韓国のスキー場のライブカメラ、積雪、天気予報。',
      ko: '한국 스키장의 라이브 카메라, 적설, 날씨 예보.',
      zh: '韩国滑雪场的实时摄像头、积雪深度和天气预报。',
    },
    sameAs: ['https://www.instagram.com/koreasnowwatch/'],
    instagram: 'https://www.instagram.com/koreasnowwatch/',
    logoPath: '/favicon-192.png',
    logoSize: 192,
    ogImagePath: '/og-image.jpg',
    contactEmail: 'hello@koreasnowwatch.com',
  },
  domain: 'koreasnowwatch.com',
  origin: 'https://koreasnowwatch.com',
  // English first: it is x-default and the root canonical (owner, 2026-11-04). Korean
  // browsers and saved choices still land on /ko/ (functions/index.js, LanguageContext).
  languages: ['en', 'ko', 'ja', 'zh'],
  crawlerLinks: true,
  crawlerResortDirectory: true,
  ogLocales: { en: 'en_GB', ja: 'ja_JP', ko: 'ko_KR', zh: 'zh_CN' },
  currency: { code: 'KRW', symbol: '₩', decimals: 0 },
  timezone: 'Asia/Seoul',
  timezoneLabel: 'KST',
  utcOffsetHours: 9,
  country: {
    code: 'KR',
    name: { en: 'Korea', ja: '韓国', ko: '한국', zh: '韩国' },
    adjective: { en: 'Korean', ja: '韓国の', ko: '한국', zh: '韩国' },
  },
  regions: [
    { id: 'Gangwon', name: { en: 'Gangwon', ja: '江原道', ko: '강원도', zh: '江原道' }, center: [37.8, 128.2] },
    { id: 'Gyeonggi', name: { en: 'Gyeonggi', ja: '京畿道', ko: '경기도', zh: '京畿道' }, center: [37.4, 127.3] },
    { id: 'Chungbuk', name: { en: 'Chungcheongbuk', ja: '忠清北道', ko: '충청북도', zh: '忠清北道' }, center: [36.8, 127.9] },
    { id: 'Jeonbuk', name: { en: 'Jeollabuk', ja: '全羅北道', ko: '전라북도', zh: '全罗北道' }, center: [35.8, 127.1] },
    { id: 'Gyeongbuk', name: { en: 'Gyeongsangbuk', ja: '慶尚北道', ko: '경상북도', zh: '庆尚北道' }, center: [36.4, 128.9] },
    { id: 'Gyeongnam', name: { en: 'Gyeongsangnam', ja: '慶尚南道', ko: '경상남도', zh: '庆尚南道' }, center: [35.3, 128.3] },
    { id: 'Jeju', name: { en: 'Jeju', ja: '済州道', ko: '제주도', zh: '济州岛' }, center: [33.4, 126.5] },
  ],
  regionAliases: {
    'seoul metropolitan area': 'Gyeonggi',
    seoul: 'Gyeonggi',
    jeollabuk: 'Jeonbuk',
    gyeongsangbuk: 'Gyeongbuk',
    gyeongsangnam: 'Gyeongnam',
    chungcheongbuk: 'Chungbuk',
  },
  map: { center: [37.2, 127.9], zoom: 8, bounds: [[33.0, 124.5], [38.7, 131.0]] },
  analytics: {
    ga4Id: 'G-609WYVME4V',
    adsenseClient: 'ca-pub-3385716674270944',
    searchConsoleVerification: [],
    // Crawler hints the old koreasnowwatch.com index.html carried for Naver (NaverBot/Yeti) and Daum.
    extraMeta: [
      { name: 'NaverBot', content: 'All' },
      { name: 'Yeti', content: 'All' },
      { name: 'daumoa', content: 'index,follow' },
      // Naver Search Advisor ownership check (owner added the site on 2026-10-25).
      { name: 'naver-site-verification', content: '04dcf93db7a11cb76173e0983844754c71e5b3ae' },
    ],
  },
  // Shared Lovable Cloud project (snow-alert sign-ups) until the site gets its own.
  supabase: {
    url: 'https://koqicactsetakozoahvh.supabase.co',
    publishableKey: 'sb_publishable_4vvTdgjilt8UUK7xYw6-WQ_FCkNtpho',
    projectId: 'koqicactsetakozoahvh',
  },
  originCity: { en: 'Seoul', ja: 'ソウル', ko: '서울', zh: '首尔' },
  railBrand: { en: 'KTX/ITX', ja: 'KTX/ITX', ko: 'KTX/ITX', zh: 'KTX/ITX' },
  transportOrigins: [
    { id: 'seoul', name: { en: 'Seoul', ja: 'ソウル', ko: '서울', zh: '首尔' }, coords: [37.5665, 126.978], airports: ['ICN', 'GMP'] },
    { id: 'busan', name: { en: 'Busan', ja: '釜山', ko: '부산', zh: '釜山' }, coords: [35.1796, 129.0756], airports: ['PUS'] },
    { id: 'daegu', name: { en: 'Daegu', ja: '大邱', ko: '대구', zh: '大邱' }, coords: [35.8714, 128.6014], airports: ['TAE'] },
    { id: 'daejeon', name: { en: 'Daejeon', ja: '大田', ko: '대전', zh: '大田' }, coords: [36.3504, 127.3845] },
    { id: 'gwangju', name: { en: 'Gwangju', ja: '光州', ko: '광주', zh: '光州' }, coords: [35.1595, 126.8526], airports: ['KWJ'] },
  ],
  features: {
    racetracks: true,
    x5Pass: true,
    muslimFriendly: true,
    skicast: false,
    currencyConverter: true,
    snowForecast: false,
    monthlySnowForecast: true,
    winterTraffic: true,
    seasonCalendar: true,
    bestResorts: true,
    snowReportSatelliteMap: true,
    snowReportTables: true,
    promoBanner: true,
    snowmakingForecast: true,
  },
  routes: {
    bestResorts: 'best-ski-resorts-korea',
    // No flagship route: the /yongpyong/ deep dive, travel guide and FAQ were folded
    // into /resort/yongpyong/ (data/pageSpotlights.ts) and 301 there (data/merged-pages.json).
  },
  // Korea's own pages (sites/kr/pages). The last four replace the shared page at the same path.
  extraRoutes: [
    { path: 'x6-plus-pass', page: 'X5PassPage' },
    { path: 'muslim-friendly-skiing', page: 'MuslimFriendlyPage' },
    { path: 'getting-around', page: 'GettingAroundPage' },
    { path: 'arrival-essentials', page: 'ArrivalEssentialsPage' },
    { path: 'where-to-stay', page: 'WhereToStayHubPage' },
    // Today's open slopes and night skiing (live data: workers/kr-slope-status).
    { path: 'today', page: 'KrTodayPage' },
    { path: 'faq', page: 'KrFaqPage' },
    { path: 'best-ski-resorts-korea', page: 'KrBestResortsPage' },
    { path: 'winter-traffic', page: 'KrWinterTrafficPage' },
    { path: 'lift-prices', page: 'KrLiftPricesPage' },
    { path: 'opening-dates', page: 'KrOpeningDatesPage' },
  ],
  nav: [
    { href: '/opening-dates', label: { en: 'Opening dates 2026-27', ja: 'オープン日 2026-27', ko: '2026-27 개장일', zh: '2026-27开放日期' } },
    { href: '/lift-prices', label: { en: 'Lift prices', ja: 'リフト券料金', ko: '리프트권 가격', zh: '缆车票价格' } },
    { href: '/racetrack/inje-speedium', label: { en: 'Race Track', ja: 'レーストラック', ko: '레이스 트랙', zh: '赛道' } },
    { href: '/muslim-friendly-skiing', label: { en: 'Muslim-friendly', ja: 'ムスリム対応', ko: '무슬림 친화', zh: '穆斯林友好' }, hideOn: ['ko'] },
    { href: '/x6-plus-pass', label: { en: 'X6+ Pass', ja: 'X6+パス', ko: 'X6+ 패스', zh: 'X6+季卡' } },
  ],
  gettingThere: { href: '/getting-around', label: { en: 'Getting there', ja: '行き方', ko: '가는 법', zh: '如何前往' } },
  crossSitePromos: [
    {
      site: 'jp',
      url: 'https://japansnowwatch.com/',
      name: { en: 'JapanSnowWatch', ja: 'JapanSnowWatch', ko: 'JapanSnowWatch', zh: 'JapanSnowWatch' },
      blurb: { en: 'Japanese ski resorts', ja: '日本のスキー場', ko: '일본 스키장', zh: '日本滑雪场' },
      cta: { en: 'Ski Japan', ja: '日本でスキー', ko: '일본에서 스키', zh: '去日本滑雪' },
      languages: ['en', 'ja', 'ko'],
    },
    {
      site: 'cn',
      url: 'https://chinasnowwatch.com/',
      name: { en: 'ChinaSnowWatch', ja: 'ChinaSnowWatch', ko: 'ChinaSnowWatch', zh: 'ChinaSnowWatch' },
      blurb: { en: 'Chinese ski resorts', ja: '中国のスキー場', ko: '중국 스키장', zh: '中国滑雪场' },
      cta: { en: 'Ski China', ja: '中国でスキー', ko: '중국에서 스키', zh: '去中国滑雪' },
      languages: ['en', 'zh', 'ko', 'ja'],
    },
  ],
  storageKeys: {
    cookieConsent: 'ksw-cookie-consent',
    language: 'koreasnow-language',
    currency: 'koreasnow-currency',
    rates: 'koreasnow-rates',
  },
  seo: {
    title: 'Korea Ski Resort Webcams & Snow Forecast | KoreaSnowWatch',
    description: 'Live webcams, snow depth and weather forecasts for Korean ski resorts.',
    keywords: 'korea ski resort, ski korea, korea snow, yongpyong, high1, vivaldi park, 한국 스키장, 스키장 웹캠, 韓国 スキー場, 韩国滑雪场',
    ogTitle: 'Korea Ski Resort Webcams & Snow Forecast | KoreaSnowWatch',
    ogDescription: 'Live webcams, snow depth and weather forecasts for Korean ski resorts.',
    twitterTitle: 'Korea Ski Resort Webcams & Snow Forecast',
    twitterDescription: 'Live webcams, snow depth and weather forecasts for Korean ski resorts.',
    htmlLang: 'ko',
    ogLocale: 'ko_KR',
    ogLocaleAlternates: ['en_GB', 'ja_JP', 'zh_CN'],
    shellHeading: 'Korea Ski Resort Webcams & Snow Forecast',
    shellParagraphs: [
      'Live webcams, snow depth and weather forecasts for ski resorts across Korea.',
      'Webcam stills refresh every few minutes and sit next to snow depth, temperature, wind speed and a seven-day snowfall forecast, so you can see conditions before committing to a drive.',
    ],
    baseKeywords: 'korea ski resort, ski korea, korea snow, yongpyong, high1, vivaldi park, 한국 스키장, 스키장 웹캠, 韓国 スキー場, 韩国滑雪场',
    rootTitle: 'Korea Ski Resort Webcams & Snow Forecast',
    rootDescription: 'Live webcams, snow depth and weather forecasts for ski resorts across Korea.',
    notFoundDescription:
      "The page you're looking for doesn't exist or has moved. Browse Korean ski resorts, live webcams and snow reports below.",
  },
  popularResorts: ['yongpyong', 'high1', 'vivaldi', 'phoenix', 'alpensia', 'konjiam', 'wellihilli'],
  // Katakana readings and other spellings people type; the site search matches them like names.
  searchAliases: {
    konjiam: ['コンジアム', 'Gonjiam'],
    jisan: ['チサン', 'ジサン', 'Jisan'],
    vivaldi: ['ビバルディ', 'Sono Vivaldi'],
    gangchon: ['カンチョン', 'エリシアンカンチョン', 'Elysian'],
    oakvalley: ['オークバレイ', 'オーク', 'Oak'],
    wellihilli: ['ウェリヒリ', 'ウェルリヒリ', 'Welli'],
    phoenix: ['ピョンチャン', 'ビョンチャン', 'Pyeongchang'],
    yongpyong: ['ヨンピョン', 'モナヨンピョン', 'モナ龍平', '竜平', 'Yongpyeong', 'Mona'],
    alpensia: ['アルペンシア'],
    high1: ['ハイワン', 'ハイワンリゾート', 'High 1'],
    o2: ['オーツー', 'オツー'],
    muju: ['ムジュ', 'トクユサン', 'ドギュサン', 'Deogyusan'],
    edenvalley: ['エデンバレー', 'エデン', 'Eden'],
  },
  weeklyReportSlugs: ['korea-snow-report'],
};

export default config;
